/**
 * Security utilities and configurations
 */

// CSRF Token management
let csrfToken: string | null = null;

export const generateCSRFToken = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  const token = Array.from(array, byte =>
    byte.toString(16).padStart(2, '0')
  ).join('');
  csrfToken = token;

  // Store in sessionStorage for persistence across page reloads
  sessionStorage.setItem('csrf_token', token);

  return token;
};

export const getCSRFToken = (): string | null => {
  if (csrfToken) return csrfToken;

  // Try to retrieve from sessionStorage
  const storedToken = sessionStorage.getItem('csrf_token');
  if (storedToken) {
    csrfToken = storedToken;
    return storedToken;
  }

  return null;
};

export const validateCSRFToken = (token: string): boolean => {
  const currentToken = getCSRFToken();
  return currentToken !== null && currentToken === token;
};

export const clearCSRFToken = (): void => {
  csrfToken = null;
  sessionStorage.removeItem('csrf_token');
};

// Input sanitization
export const sanitizeInput = (input: string): string => {
  // Basic XSS prevention - escape HTML characters
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

// Validate email format
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) && email.length <= 254;
};

// Validate password strength
export const validatePasswordStrength = (
  password: string
): {
  isValid: boolean;
  errors: string[];
} => {
  const errors: string[] = [];

  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  }

  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }

  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }

  if (!/\d/.test(password)) {
    errors.push('Password must contain at least one number');
  }

  if (!/[!@#$%^&*()_+\-=\]{};"'\\|,.<>?]/.test(password)) {
    errors.push('Password must contain at least one special character');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// HTTPS enforcement
export const enforceHTTPS = (): void => {
  if (
    typeof window !== 'undefined' &&
    window.location.protocol === 'http:' &&
    window.location.hostname !== 'localhost' &&
    window.location.hostname !== '127.0.0.1'
  ) {
    window.location.href = window.location.href.replace('http:', 'https:');
  }
};

// Security headers validation
export const validateSecurityHeaders = (): boolean => {
  if (typeof window === 'undefined') return true;

  // Check if CSP is present
  const metaTags = document.getElementsByTagName('meta');
  let hasCsp = false;

  for (let i = 0; i < metaTags.length; i++) {
    if (metaTags[i].getAttribute('http-equiv') === 'Content-Security-Policy') {
      hasCsp = true;
      break;
    }
  }

  return hasCsp;
};

// Rate limiting for client-side operations
class RateLimiter {
  private attempts: Map<string, number[]> = new Map();

  isAllowed(key: string, maxAttempts: number, windowMs: number): boolean {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];

    // Remove old attempts outside the window
    const validAttempts = attempts.filter(time => now - time < windowMs);

    if (validAttempts.length >= maxAttempts) {
      return false;
    }

    // Add current attempt
    validAttempts.push(now);
    this.attempts.set(key, validAttempts);

    return true;
  }

  reset(key: string): void {
    this.attempts.delete(key);
  }
}

export const rateLimiter = new RateLimiter();

// Session timeout management
export class SessionManager {
  private timeoutId: NodeJS.Timeout | null = null;
  private warningTimeoutId: NodeJS.Timeout | null = null;
  private lastActivity: number = Date.now();
  private readonly TIMEOUT_DURATION: number;
  private readonly WARNING_DURATION: number;

  private onTimeout?: () => void;
  private onWarning?: () => void;

  constructor(
    onTimeout?: () => void,
    onWarning?: () => void,
    timeoutDuration: number = 30 * 60 * 1000, // 30 minutes default
    warningDuration: number = 25 * 60 * 1000 // 25 minutes default
  ) {
    this.onTimeout = onTimeout;
    this.onWarning = onWarning;
    this.TIMEOUT_DURATION = timeoutDuration;
    this.WARNING_DURATION = warningDuration;
    this.setupActivityListeners();
    this.resetTimeout();
  }

  private setupActivityListeners(): void {
    if (typeof window === 'undefined') return;

    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    events.forEach(event => {
      document.addEventListener(event, this.updateActivity.bind(this), true);
    });
  }

  private updateActivity(): void {
    this.lastActivity = Date.now();
    this.resetTimeout();
  }

  private resetTimeout(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId);
    }

    // Set warning timeout
    this.warningTimeoutId = setTimeout(() => {
      this.onWarning?.();
    }, this.WARNING_DURATION);

    // Set session timeout
    this.timeoutId = setTimeout(() => {
      this.onTimeout?.();
    }, this.TIMEOUT_DURATION);
  }

  public extendSession(): void {
    this.updateActivity();
  }

  public getLastActivity(): number {
    return this.lastActivity;
  }

  public destroy(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId);
    }

    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];
    events.forEach(event => {
      document.removeEventListener(event, this.updateActivity.bind(this), true);
    });
  }
}
